import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Currency } from '@ui/src';
import {
  CustomerStat,
  DashboardValue,
  FinancialStat,
  PaymentTypeStat,
  Period,
  Response,
  TransactionStat,
  TransactionsType,
} from '../types';
import { dashboardApi } from './services';

export interface DashboardState {
  transactions: {
    values?: DashboardValue[];
    totalCount?: number;
  };
  financial: {
    values?: any;
  };
  customers: {
    values?: DashboardValue[];
  };
  paymentTypes: {
    values?: DashboardValue[];
  };
  cards: {
    isLoading: boolean;
    values?: DashboardValue[];
    requestInProgress: boolean;
  };
  item: {
    isLoading: boolean;
    value?: DashboardValue;
    requestInProgress?: boolean;
  };
  filters: {
    period?: Period;
    transationsType?: TransactionsType;
    dates?: string[];
  };
  baseCurrency?: Currency;
}

const initialState: DashboardState = {
  transactions: {},
  financial: {},
  customers: {},
  cards: {
    isLoading: true,
    requestInProgress: false,
  },
  paymentTypes: {},
  item: {
    isLoading: true,
  },
  filters: {
    period: Period.TODAY,
  },
};

export const dashboardSlice = createSlice({
  name: 'dashboard',
  initialState,
  reducers: {
    getValues: () => {},
    getValuesSuccess: (
      state,
      action: PayloadAction<{
        cards: DashboardValue[];
      }>,
    ) => {
      state.cards = {
        isLoading: false,
        requestInProgress: false,
        values: action.payload.cards,
      };
    },
    getCardsValues: (state) => {
      state.cards.requestInProgress = true;
    },
    getCardsValuesSuccess: (state, action: PayloadAction<DashboardValue[]>) => {
      state.cards.requestInProgress = false;
      state.cards.values = action.payload;
    },
    getValueById: (
      state,
      action: PayloadAction<{
        period?: Period;
        transactionsType?: TransactionsType;
        dates?: string[];
      }>,
    ) => {
      if (state.item.value) {
        state.item.requestInProgress = true;
      } else {
        state.item.isLoading = true;
      }

      state.filters = action.payload;
    },
    getValueByIdSuccess: (state, action: PayloadAction<DashboardValue>) => {
      state.item.isLoading = false;
      state.item.requestInProgress = false;
      state.item.value = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      dashboardApi.endpoints.getTransactionsList.matchFulfilled,
      (state, action: PayloadAction<Response<TransactionStat>>) => {
        state.transactions.values = action.payload.data.stats.map((item) => ({
          name: item.baseellaStatus,
          count: item.count.toString(),
          percent: item.percent.toString(),
        }));
        state.transactions.totalCount = action.payload.data.total;
      },
    );
    builder.addMatcher(
      dashboardApi.endpoints.getCustomersList.matchFulfilled,
      (state, action: PayloadAction<Response<CustomerStat>>) => {
        state.customers.values = action.payload.data.stats.map((item) => ({
          name: item.status,
          count: item.count.toString(),
          percent: item.percent.toString(),
        }));
      },
    );
    builder.addMatcher(
      dashboardApi.endpoints.getFinancialList.matchFulfilled,
      (state, action: PayloadAction<{ data: FinancialStat }>) => {
        state.financial.values = action.payload.data;
      },
    );
    builder.addMatcher(
      dashboardApi.endpoints.getDashboardBaseCurrency.matchFulfilled,
      (state, action: PayloadAction<{ data: Currency }>) => {
        state.baseCurrency = action.payload.data;
      },
    );
    builder.addMatcher(
      dashboardApi.endpoints.getPaymentTypesList.matchFulfilled,
      (state, action: PayloadAction<Response<PaymentTypeStat>>) => {
        state.paymentTypes.values = action.payload.data.stats.map((item) => ({
          name: item.paymentType,
          count: item.count.toString(),
          value: item.amountBC.toString(),
          percent: item.percent.toString(),
        }));
      },
    );
  },
});

export const {
  getValues,
  getValuesSuccess,
  getCardsValues,
  getCardsValuesSuccess,
  getValueById,
  getValueByIdSuccess,
} = dashboardSlice.actions;

export default dashboardSlice.reducer;
